import type { IPFSStorageService } from './ipfs.interface.ts'
import type { IPFSUploadResult, IPFSDownloadResult } from './ipfs.types.ts'
import { getSupabaseClient } from '@/services/database/supabase.client.ts'
import { env } from '@/config/env.ts'

/**
 * BL-C-01 mitigation: this service no longer ships the Pinata JWT to the
 * browser. All write operations (upload + delete) are proxied through
 * `/api/ipfs/*` server functions which keep the JWT in `process.env`.
 *
 * Reads continue to hit the public Pinata gateway directly because that
 * endpoint is unauthenticated and IPFS is meant to be cheaply readable
 * from anywhere — including by beneficiaries before they decrypt.
 *
 * The factory keeps the historical name `createPinataService(jwt, gateway)`
 * for binary compatibility with existing imports/tests, but the `jwt`
 * argument is now ignored.
 */

interface UploadJsonRequest {
  kind: 'json'
  name?: string
  keyvalues?: Record<string, string>
  data: Record<string, unknown>
}

interface UploadTextRequest {
  kind: 'text'
  name?: string
  mimeType?: string
  keyvalues?: Record<string, string>
  text: string
}

interface UploadFileRequest {
  kind: 'file'
  name?: string
  mimeType: string
  keyvalues?: Record<string, string>
  base64: string
}

type UploadRequest = UploadJsonRequest | UploadTextRequest | UploadFileRequest

interface UploadProxyResponse {
  cid: string
  id: string
  name: string
  size: number
  mimeType: string
}

/**
 * Same convention as `notification.service.ts`: in dev, point at a deployed
 * Vercel preview by setting `VITE_NOTIFICATION_API_BASE` to its origin. In
 * production these are same-origin.
 */
function apiBase(): string {
  return env.notificationApiBase?.trim() || ''
}
function uploadUrl(): string {
  return `${apiBase()}/api/ipfs/upload`
}
function deleteUrl(): string {
  return `${apiBase()}/api/ipfs/delete`
}

async function authHeaders(): Promise<Record<string, string>> {
  const client = getSupabaseClient()
  let {
    data: { session },
  } = await client.auth.getSession()

  if (!session) {
    const { error } = await client.auth.signInAnonymously()
    if (error) {
      throw new Error(
        `Could not start a storage session: ${error.message}. ` +
          'Enable Anonymous Sign-Ins in the Supabase dashboard: Authentication → Sign In / Providers → Anonymous.',
      )
    }
    ;({
      data: { session },
    } = await client.auth.getSession())
  }

  const token = session?.access_token
  if (!token) {
    throw new Error(
      'IPFS upload requires an active session. Refresh the page and try again.',
    )
  }
  return { Authorization: `Bearer ${token}` }
}

async function postUpload(
  payload: UploadRequest,
): Promise<UploadProxyResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeaders()),
  }
  const res = await fetch(uploadUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    let detail = ''
    try {
      const j = (await res.json()) as { error?: string }
      detail = j.error ?? ''
    } catch {
      detail = await res.text().catch(() => '')
    }
    throw new Error(`IPFS upload failed (${res.status}): ${detail}`)
  }
  return (await res.json()) as UploadProxyResponse
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer())
  let binary = ''
  // chunked to avoid Maximum call stack on large files
  const chunk = 0x8000
  for (let i = 0; i < buf.length; i += chunk) {
    binary += String.fromCharCode(...buf.subarray(i, i + chunk))
  }
  return btoa(binary)
}

function buildGatewayUrl(gateway: string, cid: string): string {
  const root = gateway.startsWith('http')
    ? gateway.replace(/\/$/, '')
    : `https://${gateway.replace(/\/$/, '')}`
  return `${root}/ipfs/${cid}`
}

async function fetchGateway(
  gateway: string,
  cid: string,
): Promise<{ res: Response; url: string }> {
  const url = buildGatewayUrl(gateway, cid)
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(
      `IPFS gateway fetch failed (${res.status} ${res.statusText}) for ${url}`,
    )
  }
  return { res, url }
}

export function createPinataService(
  _jwt: string,
  gateway: string,
): IPFSStorageService {
  return {
    async uploadFile(file, options) {
      const mimeType = file.type || 'application/octet-stream'
      const name =
        options?.name ??
        (file instanceof File ? file.name : undefined) ??
        'file.bin'
      const base64 = await blobToBase64(file)
      const result = await postUpload({
        kind: 'file',
        name,
        mimeType,
        keyvalues: options?.keyvalues,
        base64,
      })
      return result satisfies IPFSUploadResult
    },

    async uploadText(text, filename, options) {
      const result = await postUpload({
        kind: 'text',
        name: filename ?? options?.name,
        mimeType: 'text/plain',
        keyvalues: options?.keyvalues,
        text,
      })
      return result satisfies IPFSUploadResult
    },

    async uploadJSON(data, options) {
      const result = await postUpload({
        kind: 'json',
        name: options?.name,
        keyvalues: options?.keyvalues,
        data,
      })
      return result satisfies IPFSUploadResult
    },

    async download(cid) {
      const { res } = await fetchGateway(gateway, cid)
      const contentType = res.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        return {
          data: await res.json(),
          contentType,
        } satisfies IPFSDownloadResult
      }
      if (
        contentType?.startsWith('text/') ||
        contentType?.includes('charset')
      ) {
        return {
          data: await res.text(),
          contentType,
        } satisfies IPFSDownloadResult
      }
      return {
        data: await res.blob(),
        contentType,
      } satisfies IPFSDownloadResult
    },

    async downloadText(cid) {
      const { res } = await fetchGateway(gateway, cid)
      return res.text()
    },

    async downloadFile(cid) {
      const { res } = await fetchGateway(gateway, cid)
      return res.blob()
    },

    async downloadJSON<T = Record<string, unknown>>(cid: string): Promise<T> {
      const { res } = await fetchGateway(gateway, cid)
      const contentType = res.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        return (await res.json()) as T
      }
      // Pinata sometimes returns text/plain for JSON-encoded payloads
      const text = await res.text()
      return JSON.parse(text) as T
    },

    async getURL(cid) {
      return buildGatewayUrl(gateway, cid)
    },

    async delete(id) {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(await authHeaders()),
      }
      const res = await fetch(deleteUrl(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ id }),
      })
      if (!res.ok && res.status !== 204) {
        let detail = ''
        try {
          const j = (await res.json()) as { error?: string }
          detail = j.error ?? ''
        } catch {
          detail = await res.text().catch(() => '')
        }
        throw new Error(`IPFS delete failed (${res.status}): ${detail}`)
      }
    },
  }
}
